/**
 * 효과음.
 *
 * 음원 파일을 쓰지 않고 WebAudio로 만든다. 사무실에서 쓰는 앱이라 짧고
 * 작아야 하고, 저장소에 오디오 바이너리를 넣지 않아도 된다.
 *
 * AudioContext는 사용자 조작 없이 시작하면 브라우저가 정지시킨다.
 * 첫 소리를 낼 때(=이미 뭔가 조작한 뒤) 만들고, 정지 상태면 깨운다.
 */

export type SoundKind = 'message' | 'whisper' | 'join' | 'leave' | 'react';

interface Tone {
  /** 주파수(Hz). 여러 개면 순서대로 짧게 이어 낸다. */
  readonly steps: readonly number[];
  readonly stepMs: number;
  readonly gain: number;
  readonly type: OscillatorType;
}

/**
 * 소리마다 성격을 다르게 준다.
 *
 * 사무실에서 쓰는 앱이라 전부 짧고 작다. 길거나 크면 한 번 듣고 바로
 * 꺼버리게 된다.
 */
const TONES: Record<SoundKind, Tone> = {
  // 평범한 메시지: 낮게 한 번.
  message: { steps: [660], stepMs: 70, gain: 0.05, type: 'sine' },
  // 속닥: 두 음을 붙여 살짝 다르게. 옆에서 귀엣말하는 느낌.
  whisper: { steps: [880, 1180], stepMs: 45, gain: 0.035, type: 'triangle' },
  // 입장: 올라가는 두 음.
  join: { steps: [520, 780], stepMs: 60, gain: 0.04, type: 'sine' },
  // 퇴장: 내려가는 두 음.
  leave: { steps: [780, 520], stepMs: 60, gain: 0.035, type: 'sine' },
  // 리액션: 아주 짧게 톡.
  react: { steps: [1320], stepMs: 40, gain: 0.03, type: 'square' },
};

let context: AudioContext | null = null;

function audioContext(): AudioContext | null {
  const Ctor =
    window.AudioContext ??
    (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
  if (Ctor === undefined) return null;

  context ??= new Ctor();
  // 자동 재생 정책으로 정지된 상태면 깨운다. 실패해도 무음이 될 뿐이다.
  if (context.state === 'suspended') void context.resume().catch(() => {});
  return context;
}

export function playSound(kind: SoundKind): void {
  const ctx = audioContext();
  if (ctx === null) return;

  const tone = TONES[kind];
  const now = ctx.currentTime;

  tone.steps.forEach((frequency, index) => {
    const start = now + (index * tone.stepMs) / 1000;
    const end = start + tone.stepMs / 1000;

    const oscillator = ctx.createOscillator();
    const amp = ctx.createGain();

    oscillator.type = tone.type;
    oscillator.frequency.value = frequency;

    // 끝을 뭉툭하게 자르면 '툭' 하는 잡음이 난다. 짧게 감쇠시킨다.
    amp.gain.setValueAtTime(0, start);
    amp.gain.linearRampToValueAtTime(tone.gain, start + 0.008);
    amp.gain.exponentialRampToValueAtTime(0.0001, end);

    oscillator.connect(amp).connect(ctx.destination);
    oscillator.start(start);
    oscillator.stop(end + 0.02);
  });
}
